import { runAuthPolicyHook } from '@xstudios/xapp-auth/services/authPolicyHooks'
import {
  loginWithGoogle as apiGoogleLogin,
  login as apiLogin,
  logout as apiLogout,
  refresh as apiRefresh,
  cancelTokenRefresh,
  isTokenExpired,
  scheduleTokenRefresh,
} from '@xstudios/xapp-auth/services/googleJwtAuthService'
import { defineStore } from 'pinia'

export const useGoogleJwtAuthStore = defineStore('google-jwt-auth', {
  actions: {
    _matches(userPerm, requiredPattern) {
      // true if user's grant matches the required pattern, OR the pattern wildcard matches the grant
      const [uNs, uAct] = this._splitPerm(userPerm)
      const [rNs, rAct] = this._splitPerm(requiredPattern)
      const nsOk = uNs === '*' || rNs === '*' || uNs === rNs
      const actOk = uAct === '*' || rAct === '*' || uAct === rAct
      return nsOk && actOk
    },

    // --- helpers ---
    _splitPerm(s) {
      const [ns = '*', act = '*'] = String(s).toLowerCase().split(':')
      return [ns || '*', act || '*']
    },

    async bootstrap() {
      if (this.bootstrapped) {
        return
      }
      await this.rehydrateSession()
      this.bootstrapped = true
    },

    // single check
    can(required) {
      if (!required) {
        return false
      }
      if (this.effectiveIsSuperuser) {
        return true
      }
      const perms = this.effectivePermissions
      if (perms.includes('*') || perms.includes('*:*')) {
        return true
      }
      for (const grant of perms) {
        if (this._matches(grant, required)) {
          return true
        }
      }
      return false
    },

    // all-of
    canAll(requiredList) {
      if (!Array.isArray(requiredList) || requiredList.length === 0) {
        return true
      }
      return requiredList.every((p) => this.can(p))
    },

    // any-of
    canAny(requiredList) {
      if (!Array.isArray(requiredList) || requiredList.length === 0) {
        return false
      }
      return requiredList.some((p) => this.can(p))
    },

    // DEV-ONLY: stop simulating a role. Real session state (user/permissions)
    // was never touched, so this simply drops the override.
    clearDevSimulatedRole() {
      this.devSimulatedRole = null
    },

    clearSession() {
      this.setToken(null)
      this.refreshToken = null
      this.refreshInFlight = null
      this.user = null
      this.permissions = []
    },

    isAuthenticated() {
      return this.authenticated
    },

    async login(email, password) {
      const session = await apiLogin(email, password)
      this.setSession(session)
    },

    async loginWithGoogle(idToken) {
      const session = await apiGoogleLogin(idToken)
      this.setSession(session)
    },

    async logout() {
      try {
        await apiLogout()
      } catch {
        // no-op: local logout should always proceed
      }
      this.clearSession()
    },

    onHydrated() {
      this.hydrated = true
      if (this.bootstrapped) {
        void this.rehydrateSession()
      }
    },

    async rehydrateSession() {
      if (this.rehydrateInFlight) {
        return this.rehydrateInFlight
      }

      this.rehydrateInFlight = (async () => {
        if (this.refreshToken) {
          await this.silentRefresh()
          return
        }

        if (this.token && !isTokenExpired(this.token)) {
          this.setToken(this.token)
          return
        }

        if (this.token) {
          this.clearSession()
        }
      })()

      try {
        await this.rehydrateInFlight
      } finally {
        this.rehydrateInFlight = null
      }
    },

    // DEV-ONLY: simulate viewing the app as a different role, without ever
    // touching real session state (user/permissions). Never persisted, so a
    // plain refresh always restores the real session.
    setDevSimulatedRole(role) {
      if (!import.meta.env.DEV || !role?.role) {
        return
      }
      this.devSimulatedRole = {
        key: role.role,
        label: role.label || role.role,
        permissions: Array.isArray(role.permissions)
          ? role.permissions.map((p) => String(p).trim().toLowerCase())
          : [],
      }
    },

    setSession({ access_token, permissions, refresh_token, user }) {
      this.setToken(access_token)
      this.refreshToken = refresh_token || null
      this.user = user
      // normalize & dedupe
      this.permissions = [
        ...new Set(
          (permissions || []).map((p) => String(p).trim().toLowerCase())
        ),
      ]
    },

    setToken(t) {
      this.token = t
      this.authenticated = !!t

      // Schedule auto-refresh when token is set
      if (t) {
        this.startRefreshScheduler(t)
      } else {
        this.stopRefreshScheduler()
      }
    },

    async silentRefresh() {
      if (this.refreshInFlight) {
        return this.refreshInFlight
      }
      if (!this.refreshToken) {
        this.clearSession()
        void runAuthPolicyHook('onRefreshFailed', {})
        return false
      }

      this.refreshInFlight = (async () => {
        try {
          const session = await apiRefresh(this.refreshToken)
          this.setSession(session)
          return true
        } catch {
          this.clearSession()
          void runAuthPolicyHook('onRefreshFailed', {})
          return false
        } finally {
          this.refreshInFlight = null
        }
      })()

      return this.refreshInFlight
    },

    startRefreshScheduler(token) {
      scheduleTokenRefresh(token)
    },

    stopRefreshScheduler() {
      cancelTokenRefresh()
    },
  },
  getters: {
    // DEV-ONLY overrides used by can()/canAll()/canAny() so the whole app's
    // permission checks (nav, v-perm, router guard) respect an active role
    // simulation without ever touching the real `user`/`permissions` state.
    effectiveIsSuperuser(state) {
      if (import.meta.env.DEV && state.devSimulatedRole) {
        return state.devSimulatedRole.key === 'superuser'
      }
      return !!state.user?.is_superuser
    },
    effectivePermissions(state) {
      if (import.meta.env.DEV && state.devSimulatedRole) {
        return state.devSimulatedRole.permissions
      }
      return state.permissions
    },
    isDevRoleSimActive(state) {
      return import.meta.env.DEV && !!state.devSimulatedRole
    },
  },
  persist: {
    afterHydrate: (ctx) => {
      ctx.store.onHydrated()
    },
    key: 'account-auth',
    pick: ['token', 'refreshToken', 'user', 'permissions', 'authenticated'],
  },
  state: () => ({
    authenticated: false,
    bootstrapped: false,
    // DEV-ONLY, never persisted (see `pick` above): { key, label, permissions }
    devSimulatedRole: null,
    hydrated: false,
    permissions: [],
    refreshInFlight: null,
    refreshToken: null,
    rehydrateInFlight: null,
    token: null,
    user: null,
  }),
})
